import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { useEntitlements } from '../app/hooks/useEntitlements';
import { motion } from 'framer-motion';
import { RootState } from '../app/store';
import { addExpense, updateExpense } from '../app/slices/expenseSlice';
import { addToast } from '../app/slices/uiSlice';
import { ExpenseCategory, EXPENSE_TAGS } from '../types/models';
import { GSTCalculator } from '../lib/calculateGST';
import ExpenseTagSelector from './ExpenseTagSelector';
import ReceiptUploader from './ReceiptUploader';
import LoadingSpinner from './LoadingSpinner';
import { EXPENSE_EXAMPLES } from '../config/expenseExamples';
import {
  TruckIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  PhoneIcon,
  ShieldCheckIcon,
  MapPinIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';

// Helper function to safely parse numbers, returning 0 for invalid values
const safeParseFloat = (value: string | number): number => {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Category-aware description tags
const SUGGESTED_TAGS: Record<
  ExpenseCategory,
  Array<{ label: string; value: string; gstClaimable?: boolean; icon?: React.ComponentType<any> }>
> = {
  fuel: [
    {
      label: 'Gas — Shell station',
      value: 'Gas -- Shell station',
      gstClaimable: true,
      icon: TruckIcon,
    },
    {
      label: 'Gas — Esso station',
      value: 'Gas -- Esso station',
      gstClaimable: true,
      icon: TruckIcon,
    },
    {
      label: 'Gas — Chevron station',
      value: 'Gas -- Chevron station',
      gstClaimable: true,
      icon: TruckIcon,
    },
    {
      label: 'Gas — Petro-Canada',
      value: 'Gas -- Petro-Canada station',
      gstClaimable: true,
      icon: TruckIcon,
    },
    {
      label: 'Supercharge — Tesla Supercharger',
      value: 'Supercharge -- Tesla Supercharger station',
      gstClaimable: true,
      icon: TruckIcon,
    },
    {
      label: 'Home Charging — Residential EV',
      value: 'Home Charging -- Residential EV charging (calculate from electric bill)',
      gstClaimable: true,
      icon: TruckIcon,
    },
    {
      label: 'Public EV Charging',
      value: 'Public EV Charging -- Third-party charging station',
      gstClaimable: true,
      icon: TruckIcon,
    },
  ],
  maintenance: [
    {
      label: 'Oil change at',
      value: 'Oil change at ',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Tires at',
      value: 'Tires at ',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Tire Balancing at',
      value: 'Tire Balancing at ',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Brakes at',
      value: 'Brakes at ',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Filters at',
      value: 'Filters at ',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Commercial Inspection at',
      value: 'Commercial Inspection at ',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Wipers at',
      value: 'Wiper blades at ',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
  ],
  'vehicle-upkeep': [
    {
      label: 'Car wash — Touchless',
      value: 'Car wash at Touchless',
      gstClaimable: true,
      icon: CogIcon,
    },
    {
      label: 'Detailing (interior)',
      value: 'Interior car detailing',
      gstClaimable: true,
      icon: CogIcon,
    },
    { label: 'Tire rotation', value: 'Tire rotation service', gstClaimable: true, icon: CogIcon },
  ],
  'phone-data': [
    {
      label: 'Mobile plan — Telus',
      value: 'Mobile plan with Telus',
      gstClaimable: false,
      icon: PhoneIcon,
    },
    { label: 'Data add-on', value: 'Data add-on package', gstClaimable: false, icon: PhoneIcon },
    {
      label: 'Phone case (work)',
      value: 'Protective phone case for work use',
      gstClaimable: true,
      icon: PhoneIcon,
    },
  ],
  insurance: [
    {
      label: 'Commercial rideshare rider',
      value: 'Commercial rideshare insurance rider',
      gstClaimable: true,
      icon: ShieldCheckIcon,
    },
    {
      label: 'Broker service fee (GST only ITC)',
      value: 'Insurance broker service fee',
      gstClaimable: true,
      icon: ShieldCheckIcon,
    },
  ],
  miscellaneous: [
    {
      label: 'Parking — downtown',
      value: 'Downtown parking',
      gstClaimable: false,
      icon: MapPinIcon,
    },
    { label: 'Airport lot', value: 'Airport parking lot', gstClaimable: false, icon: MapPinIcon },
    { label: 'Tolls — bridge', value: 'Bridge tolls', gstClaimable: false, icon: MapPinIcon },
  ],
  meals: [
    {
      label: 'Meal (personal) — not deductible',
      value: 'Personal meal - not business deductible',
      gstClaimable: false,
      icon: NoSymbolIcon,
    },
  ],
  entertainment: [
    {
      label: 'Entertainment (personal) — not deductible',
      value: 'Personal entertainment - not business deductible',
      gstClaimable: false,
      icon: NoSymbolIcon,
    },
  ],
  accessories: [],
  clothing: [],
  'lease-payment': [],
  'loan-interest': [],
  'tax-services': [],
  custom: [
    {
      label: 'Car repair itemized',
      value: 'Car repair: [itemize each part/service separately]',
      gstClaimable: true,
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Vehicle part',
      value: 'Vehicle part: ',
      gstClaimable: true,
      icon: CogIcon,
    },
  ],
};

interface ExpenseFormProps {
  mode: 'create' | 'edit';
  expenseId?: string | null;
  onCancel: () => void;
  onSaved: (saved: any) => void;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({ mode, expenseId, onCancel, onSaved }) => {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { expenses, isLoading, uploadState } = useSelector((state: RootState) => state.expenses);
  const { canWrite, hasArchiveAccess } = useEntitlements();

  const existingExpense = expenseId ? expenses.find(e => e.id === expenseId) : null;
  const isEditing = mode === 'edit' && !!existingExpense;

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '' as ExpenseCategory | '',
    subCategory: '',
    amount: '',
    gstPaid: '',
    businessUsePct: '100',
    description: '',
    qualifies50Pct: false,
    mealContext: undefined as any,
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with existing expense data
  useEffect(() => {
    if (existingExpense) {
      setFormData({
        date: new Date(existingExpense.date).toISOString().split('T')[0],
        category: existingExpense.category,
        subCategory: existingExpense.subCategory,
        amount: existingExpense.amount.toString(),
        gstPaid: existingExpense.gstPaid.toString(),
        businessUsePct: existingExpense.businessUsePct.toString(),
        description: existingExpense.description,
        qualifies50Pct: existingExpense.qualifies50Pct || false,
        mealContext: existingExpense.mealContext,
      });
    }
  }, [existingExpense]);

  // Auto-calculate GST when amount changes
  useEffect(() => {
    if (formData.amount && user?.province && !formData.gstPaid) {
      try {
        const calculator = new GSTCalculator(user.province);
        const amount = parseFloat(formData.amount);
        if (!isNaN(amount) && amount > 0) {
          const gst = calculator.calculateGSTFromTotal(amount);
          setFormData(prev => ({
            ...prev,
            gstPaid: gst.toFixed(2),
          }));
        }
      } catch (error) {
        console.error('Error calculating GST:', error);
      }
    }
  }, [formData.amount, formData.gstPaid, user?.province]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Date validation
    if (!formData.date) {
      newErrors.date = 'Date is required';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      if (selectedDate > today) {
        newErrors.date = 'Date cannot be in the future';
      }
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.subCategory) {
      newErrors.subCategory = 'Tag is required';
    }

    // Amount validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valid amount is required';
    }

    // GST validation - required if amount is entered
    const gstPaid = parseFloat(formData.gstPaid);
    const amount = parseFloat(formData.amount);
    if (amount > 0) {
      if (!formData.gstPaid || formData.gstPaid.trim() === '') {
        newErrors.gstPaid = 'GST/HST amount is required (enter 0 if none)';
      } else if (isNaN(gstPaid) || gstPaid < 0) {
        newErrors.gstPaid = 'GST/HST paid must be a valid positive number or 0';
      }
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    const businessUsePct = parseFloat(formData.businessUsePct);
    if (isNaN(businessUsePct) || businessUsePct < 0 || businessUsePct > 100) {
      newErrors.businessUsePct = 'Business use must be between 0 and 100%';
    }

    // Prompt for strong description if business use is 100%
    if (businessUsePct === 100 && formData.description.trim().length < 20) {
      newErrors.description =
        'For 100% business use, provide a detailed description explaining why this expense was entirely business-related (minimum 20 characters)';
    }

    // Receipt validation (REQUIRED for new expenses)
    if (!receiptFile && mode === 'create') {
      newErrors.receipt = 'Receipt photo is required. Please upload a photo of your receipt.';
    }

    // Special validation for phone expenses - prompt for business %
    if (formData.category === 'phone-data' && businessUsePct === 100) {
      newErrors.businessUsePct =
        'Phone expenses typically have partial business use. Please specify the business percentage.';
    }

    // Special validation for Meals/Entertainment - block by default
    if (formData.category === 'meals' || formData.category === 'entertainment') {
      const meetsNarrowException =
        Boolean(formData.mealContext?.overnight) &&
        (formData.mealContext?.distanceMiles ?? 0) >= 100 &&
        !!formData.mealContext?.hotelReceiptUrl?.trim() &&
        !!formData.mealContext?.meetingDetails?.trim();

      if (!meetsNarrowException) {
        newErrors.category =
          'Meals/Entertainment expenses are blocked by default due to high audit risk. Only narrow exceptions are permitted.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('[ExpenseForm] Submit initiated with formData:', {
      date: formData.date,
      category: formData.category,
      subCategory: formData.subCategory,
      amount: formData.amount,
      gstPaid: formData.gstPaid,
      description: formData.description.substring(0, 50),
    });

    // CRITICAL: Check if user has set their province (required for tax calculations)
    if (!user?.province || user.province === '') {
      dispatch(
        addToast({
          message:
            'Please set your province in Profile Settings before adding expenses. Tax rates vary by province!',
          type: 'error',
        })
      );
      return;
    }

    if (!validateForm() || !user?.uid) {
      console.error('[ExpenseForm] Validation failed or no user');

      // Show specific error for missing receipt
      if (mode === 'create' && !receiptFile) {
        dispatch(
          addToast({
            message: 'Receipt photo is required to create an expense',
            type: 'error',
          })
        );
      }
      return;
    }

    // Check write permissions
    if (!canWrite) {
      dispatch(
        addToast({
          message: 'Cannot save expense: Read-only access enabled.',
          type: 'warning',
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // CAPTURE formData in local variables to prevent race conditions
      const capturedFormData = {
        date: formData.date,
        category: formData.category,
        subCategory: formData.subCategory,
        amount: formData.amount,
        gstPaid: formData.gstPaid,
        businessUsePct: formData.businessUsePct,
        description: formData.description,
        qualifies50Pct: formData.qualifies50Pct,
        mealContext: formData.mealContext,
      };

      console.log('[ExpenseForm] Captured form data:', capturedFormData);

      // For M&E, determine qualifies50Pct based on narrow exception criteria
      let qualifies50Pct = capturedFormData.qualifies50Pct;
      if (capturedFormData.category === 'meals' || capturedFormData.category === 'entertainment') {
        qualifies50Pct =
          Boolean(capturedFormData.mealContext?.overnight) &&
          (capturedFormData.mealContext?.distanceMiles ?? 0) >= 100 &&
          !!capturedFormData.mealContext?.hotelReceiptUrl?.trim() &&
          !!capturedFormData.mealContext?.meetingDetails?.trim();
      }

      // Triple-check validation before creating expense data
      if (!capturedFormData.category) {
        console.error('[ExpenseForm] Category is empty after capture, cannot submit');
        dispatch(
          addToast({
            message: 'Please select a category',
            type: 'error',
          })
        );
        setIsSubmitting(false);
        return;
      }

      if (!capturedFormData.date) {
        console.error('[ExpenseForm] Date is empty after capture, cannot submit');
        dispatch(
          addToast({
            message: 'Please select a date',
            type: 'error',
          })
        );
        setIsSubmitting(false);
        return;
      }

      const amount = parseFloat(capturedFormData.amount);
      if (isNaN(amount) || amount <= 0) {
        console.error('[ExpenseForm] Amount is invalid after capture, cannot submit');
        dispatch(
          addToast({
            message: 'Please enter a valid amount',
            type: 'error',
          })
        );
        setIsSubmitting(false);
        return;
      }

      const expenseData = {
        date: new Date(capturedFormData.date),
        category: capturedFormData.category as ExpenseCategory,
        subCategory: capturedFormData.subCategory,
        amount: amount,
        gstPaid: parseFloat(capturedFormData.gstPaid) || 0,
        businessUsePct: parseFloat(capturedFormData.businessUsePct),
        description: capturedFormData.description.trim(),
        source: 'manual' as const,
        qualifies50Pct,
        mealContext: capturedFormData.mealContext,
      };

      console.log('[ExpenseForm] Final expenseData to save:', {
        ...expenseData,
        date: expenseData.date.toISOString(),
      });

      if (isEditing && expenseId) {
        const action: any = await dispatch(
          updateExpense({
            userId: user.uid,
            expenseId,
            updates: expenseData,
            provinceCode: user.province,
          })
        );
        const saved = action.payload;
        dispatch(
          addToast({
            message: 'Expense updated successfully',
            type: 'success',
          })
        );
        if (saved) onSaved(saved);
      } else {
        const action: any = await dispatch(
          addExpense({
            userId: user.uid,
            expenseData,
            receiptFile: receiptFile || undefined,
            provinceCode: user.province,
          })
        );
        const saved = action.payload;
        dispatch(
          addToast({
            message: 'Expense added successfully',
            type: 'success',
          })
        );
        if (saved) onSaved(saved);
      }
    } catch (error: any) {
      dispatch(
        addToast({
          message: error.message || 'Failed to save expense',
          type: 'error',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const exampleChips: string[] = React.useMemo(() => {
    const cat = formData.category;
    if (!cat) return [];
    switch (cat) {
      case 'fuel':
        return EXPENSE_EXAMPLES.FUEL || [];
      case 'maintenance':
        return EXPENSE_EXAMPLES.MAINT || [];
      case 'vehicle-upkeep':
        return EXPENSE_EXAMPLES.UPKEEP || [];
      case 'insurance':
        return EXPENSE_EXAMPLES.INSURANCE || [];
      case 'phone-data':
        return EXPENSE_EXAMPLES.PHONE || [];
      case 'accessories':
        return EXPENSE_EXAMPLES.ACCESSORIES || [];
      case 'lease-payment':
        return EXPENSE_EXAMPLES.LEASE_PAYMENT || [];
      case 'loan-interest':
        return EXPENSE_EXAMPLES.LOAN_INTEREST || [];
      case 'tax-services':
        return EXPENSE_EXAMPLES.TAX_SERVICES || [];
      case 'miscellaneous': {
        const arr: string[] = [];
        if (EXPENSE_EXAMPLES.PARKING) arr.push(...EXPENSE_EXAMPLES.PARKING);
        if (EXPENSE_EXAMPLES.TOLLS) arr.push(...EXPENSE_EXAMPLES.TOLLS);
        return arr;
      }
      default:
        return [];
    }
  }, [formData.category]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="space-y-6"
    >
      {/* Read-only banner for ARCHIVE users */}
      {hasArchiveAccess && !canWrite && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Read-Only Mode</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Your account has Archive access. You can view expense details but cannot make
                  changes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date and Amount */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="label">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-2">
                1
              </span>
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={e => handleInputChange('date', e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className={`input ${errors.date ? 'input-error' : ''}`}
              title="Select a date (future dates not allowed)"
            />
            {errors.date && <p className="error-text">{errors.date}</p>}
          </div>

          <div>
            <label className="label">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-2">
                2
              </span>
              Total Amount (CAD)
            </label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={formData.amount}
              onChange={e => handleInputChange('amount', e.target.value.replace(',', '.'))}
              className={`input ${errors.amount ? 'input-error' : ''}`}
            />
            {errors.amount && <p className="error-text">{errors.amount}</p>}
          </div>
        </div>

        {/* Category and Tag Selection */}
        <div className="space-y-2">
          <label className="label">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-2">
              3
            </span>
            Expense Category & Tag
          </label>
          <ExpenseTagSelector
            selectedCategory={formData.category || undefined}
            selectedTag={formData.subCategory}
            onCategoryChange={category => {
              handleInputChange('category', category);
              handleInputChange('subCategory', ''); // Reset tag when category changes
            }}
            onTagChange={tag => handleInputChange('subCategory', tag)}
          />
          {errors.category && <p className="error-text">{errors.category}</p>}
          {errors.subCategory && <p className="error-text">{errors.subCategory}</p>}
        </div>

        {/* GST and Business Use */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="label">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-2">
                4
              </span>
              GST/HST Paid (CAD)
            </label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={formData.gstPaid}
              onChange={e => handleInputChange('gstPaid', e.target.value.replace(',', '.'))}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Auto-calculated from total amount. Adjust if needed.
            </p>
          </div>

          <div>
            <label className="label">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-2">
                5
              </span>
              Business Use % (Your Assessment)
            </label>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={formData.businessUsePct}
                  onChange={e => handleInputChange('businessUsePct', e.target.value)}
                  className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-business-use ${errors.businessUsePct ? 'input-error' : ''}`}
                  style={{
                    background: `linear-gradient(to right,
                      #22c55e 0% 50%,
                      #eab308 50% 85%,
                      #dc2626 85% 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span className="font-semibold text-green-600">50% (Business Case)</span>
                  <span>100%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">{formData.businessUsePct}%</div>
                <div className="flex items-center space-x-2">
                  {parseInt(formData.businessUsePct) >= 0 &&
                    parseInt(formData.businessUsePct) <= 50 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                        ✓ OK risk
                      </span>
                    )}
                  {parseInt(formData.businessUsePct) >= 51 &&
                    parseInt(formData.businessUsePct) <= 85 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                        ⚠ Higher risk
                      </span>
                    )}
                  {parseInt(formData.businessUsePct) >= 86 && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-red-100 text-red-900 border-2 border-red-500 shadow-lg animate-pulse">
                      ⛔ Very high risk
                    </span>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <div className="flex items-start space-x-2">
                  <svg
                    className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-medium text-blue-900 mb-1">Your Knowledge Matters</p>
                    <p className="text-blue-800">
                      <strong>50% is our recommended conservative baseline</strong>, but you as the
                      driver have the ultimate knowledge of how this expense was used. Higher
                      percentages significantly increase audit risk. Be prepared to justify your
                      choice.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {errors.businessUsePct && <p className="error-text">{errors.businessUsePct}</p>}
          </div>
        </div>

        {/* Warning for No GST items - Very High Risk */}
        {(() => {
          // Check if selected tag is not GST claimable
          const selectedTagData =
            formData.category && formData.subCategory
              ? EXPENSE_TAGS[formData.category]?.find(tag => tag.name === formData.subCategory)
              : null;
          const isNoGST = selectedTagData && !selectedTagData.gstClaimable;

          if (isNoGST) {
            return (
              <div className="rounded-xl border-2 border-red-500 bg-red-50 p-4 shadow-lg animate-pulse">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-red-900">
                      ⛔ Very High Risk — No GST/HST Claimable
                    </h3>
                    <p className="mt-2 text-sm text-red-800">
                      <strong>This expense category has NO GST/HST to claim.</strong> Recording this
                      expense will NOT generate any Input Tax Credits (ITC). This should be avoided
                      in most cases unless you have a legitimate business reason and understand the
                      audit implications.
                    </p>
                    <div className="mt-3 text-sm text-red-900 bg-red-100 rounded-md p-2 border border-red-300">
                      <strong>⚠️ Warning:</strong> High business use % on non-GST items
                      significantly increases CRA audit risk. Consider carefully before proceeding.
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Meals & Entertainment — Very High Audit Risk (Blocked) */}
        {(formData.category === 'meals' || formData.category === 'entertainment') && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 space-y-2">
            <div className="font-semibold">
              Meals & Entertainment — Very High Audit Risk (Blocked)
            </div>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>
                <strong>No ITC</strong> is allowed on Meals/Entertainment in this product.
              </li>
              <li>
                Only narrow exception: <strong>overnight business trip</strong>{' '}
                <strong>&gt; 100 miles / 160 km</strong> from home with <strong>hotel stay</strong>{' '}
                and a <strong>documented business meeting</strong>.
              </li>
              <li>
                If you can't meet and prove all criteria, <strong>do not claim</strong>.
              </li>
            </ul>
            <button
              type="button"
              onClick={() => window.location.assign('/learn#meals-entertainment')}
              className="mt-1 inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-white hover:bg-red-700"
            >
              Learn the rules
            </button>

            {/* Guarded override */}
            <details className="mt-3">
              <summary className="cursor-pointer text-sm underline">
                I meet the narrow exception (show fields)
              </summary>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="distanceMiles"
                    className="block text-sm font-medium text-red-900 mb-1"
                  >
                    Distance from home (miles)
                  </label>
                  <input
                    type="number"
                    id="distanceMiles"
                    min="0"
                    value={formData.mealContext?.distanceMiles || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        mealContext: {
                          ...prev.mealContext,
                          distanceMiles: parseFloat(e.target.value) || undefined,
                        },
                      }));
                    }}
                    placeholder="Must be ≥ 100"
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm placeholder-red-400 focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="meetingDateTime"
                    className="block text-sm font-medium text-red-900 mb-1"
                  >
                    Meeting date & time
                  </label>
                  <input
                    type="datetime-local"
                    id="meetingDateTime"
                    value={formData.mealContext?.meetingDateTime || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        mealContext: {
                          ...prev.mealContext,
                          meetingDateTime: e.target.value,
                        },
                      }));
                    }}
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="overnight"
                      checked={formData.mealContext?.overnight || false}
                      onChange={e => {
                        setFormData(prev => ({
                          ...prev,
                          mealContext: {
                            ...prev.mealContext,
                            overnight: e.target.checked,
                          },
                        }));
                      }}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label htmlFor="overnight" className="ml-2 text-sm text-red-800">
                      Overnight stay required
                    </label>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="hotelReceiptUrl"
                    className="block text-sm font-medium text-red-900 mb-1"
                  >
                    Hotel receipt URL
                  </label>
                  <input
                    type="url"
                    id="hotelReceiptUrl"
                    value={formData.mealContext?.hotelReceiptUrl || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        mealContext: {
                          ...prev.mealContext,
                          hotelReceiptUrl: e.target.value,
                        },
                      }));
                    }}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm placeholder-red-400 focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="meetingDetails"
                    className="block text-sm font-medium text-red-900 mb-1"
                  >
                    Meeting details
                  </label>
                  <input
                    type="text"
                    id="meetingDetails"
                    value={formData.mealContext?.meetingDetails || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        mealContext: {
                          ...prev.mealContext,
                          meetingDetails: e.target.value,
                        },
                      }));
                    }}
                    placeholder="Attendees, purpose, agenda"
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm placeholder-red-400 focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>
              </div>
            </details>
          </div>
        )}

        {/* Custom Category Guidance */}
        {formData.category === 'custom' && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <h4 className="text-sm font-semibold text-blue-900">
              💡 Custom Category — Itemization Best Practices
            </h4>
            <ul className="list-disc pl-5 text-sm text-blue-800 space-y-1">
              <li>
                <strong>Itemize car repairs:</strong> Instead of "Car repairs - $2,000", list each
                part/service separately (e.g., "Brake pads $350, Rotors $280, Labor $370").
              </li>
              <li>
                <strong>Attach the receipt photo</strong> to support your itemized breakdown.
              </li>
              <li>Cars have many parts — use this category when standard categories don't fit.</li>
              <li>
                <strong>Be specific:</strong> Detailed descriptions reduce audit risk and improve
                accuracy.
              </li>
            </ul>
          </div>
        )}

        {/* Description */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="label mb-0">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-2">
                6
              </span>
              Description{' '}
              {parseInt(formData.businessUsePct) === 100 && (
                <span className="text-xs font-normal text-amber-600">
                  (Explain why 100% business use)
                </span>
              )}
            </label>
            <span
              className={`text-xs font-medium ${
                parseInt(formData.businessUsePct) === 100 && formData.description.trim().length < 20
                  ? 'text-amber-600'
                  : formData.description.trim().length >= 20
                    ? 'text-green-600'
                    : 'text-gray-500'
              }`}
            >
              {formData.description.trim().length} /{' '}
              {parseInt(formData.businessUsePct) === 100 ? '20 min' : 'unlimited'}
            </span>
          </div>
          <textarea
            rows={3}
            placeholder="Brief description of the expense..."
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            className={`input ${errors.description ? 'input-error' : ''}`}
          />
          {errors.description && <p className="error-text">{errors.description}</p>}
          {parseInt(formData.businessUsePct) === 100 &&
            formData.description.trim().length < 20 &&
            formData.description.trim().length > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                {20 - formData.description.trim().length} more characters needed for 100% business
                use
              </p>
            )}

          {/* Category-aware suggested tags */}
          {formData.category && SUGGESTED_TAGS[formData.category]?.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-2">Suggested descriptions:</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_TAGS[formData.category].map((tag, index) => {
                  const IconComponent = tag.icon;
                  return (
                    <button
                      key={index}
                      type="button"
                      className="inline-flex items-center gap-1 rounded-2xl border border-gray-200 bg-white px-3 py-1 text-sm shadow-sm hover:shadow-md hover:bg-gray-50 transition-all duration-200 hover:scale-105"
                      onClick={() => {
                        const currentDesc = formData.description.trim();
                        const newValue = currentDesc ? `${currentDesc} - ${tag.value}` : tag.value;
                        handleInputChange('description', newValue);
                      }}
                      title={
                        tag.gstClaimable !== undefined
                          ? `GST ${tag.gstClaimable ? 'claimable' : 'not claimable'}`
                          : undefined
                      }
                      aria-label={`Add: ${tag.label}`}
                    >
                      {IconComponent && <IconComponent className="h-3 w-3 text-gray-500" />}
                      <span>{tag.label}</span>
                      {tag.gstClaimable !== undefined && (
                        <span
                          className={`text-xs px-1 rounded ${tag.gstClaimable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                        >
                          {tag.gstClaimable ? 'GST' : 'No GST'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legacy example chips as fallback */}
          {(!formData.category || !SUGGESTED_TAGS[formData.category]?.length) &&
            exampleChips.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {exampleChips.map(txt => (
                  <button
                    key={txt}
                    type="button"
                    className="text-xs rounded-full border px-2 py-1 hover:bg-muted"
                    onClick={() => {
                      if (!formData.description.trim()) {
                        handleInputChange('description', txt);
                      }
                    }}
                    aria-label={`Use example: ${txt}`}
                  >
                    {txt}
                  </button>
                ))}
              </div>
            )}
        </div>

        {/* Receipt Upload */}
        {!isEditing && (
          <div>
            <label className="label mb-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold mr-2">
                7
              </span>
              Receipt Photo <span className="text-red-600 font-bold">*REQUIRED*</span>
            </label>
            <ReceiptUploader
              onFileSelect={setReceiptFile}
              onFileRemove={() => setReceiptFile(null)}
              selectedFile={receiptFile || undefined}
              isUploading={
                uploadState.status === 'uploading' || uploadState.status === 'compressing'
              }
            />

            {/* Receipt Required Message */}
            {!receiptFile && (
              <div className="mt-3 bg-red-50 border-2 border-red-300 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <svg
                    className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900">Receipt Required</p>
                    <p className="text-xs text-red-800 mt-1">
                      You must upload a receipt photo to create an expense. This is required for CRA
                      audit compliance and record keeping.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Show validation error if user tries to submit without receipt */}
            {errors.receipt && (
              <p className="mt-2 text-sm text-red-600 font-semibold">{errors.receipt}</p>
            )}

            {/* Upload progress indicator */}
            {receiptFile && uploadState.status !== 'idle' && uploadState.status !== 'done' && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-blue-900">
                    {uploadState.status === 'compressing' && '📦 Compressing receipt...'}
                    {uploadState.status === 'uploading' && '☁️ Uploading to cloud...'}
                    {uploadState.status === 'stalled' && '⏳ Upload taking longer than expected...'}
                    {uploadState.status === 'failed' && '❌ Upload failed'}
                  </span>
                  <span className="text-blue-700 font-semibold">{uploadState.progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadState.progress}%` }}
                  />
                </div>
                {uploadState.status === 'stalled' && (
                  <p className="text-xs text-blue-700 mt-2">
                    Please wait, or check your internet connection...
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Calculated ITC Preview */}
        {formData.gstPaid && formData.businessUsePct && user?.province && (
          <div className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">💰 Calculation Preview</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <span className="text-gray-600 block mb-1">Claimable ITC:</span>
                <span className="text-xl font-bold text-green-700">
                  $
                  {(
                    (safeParseFloat(formData.gstPaid) * safeParseFloat(formData.businessUsePct)) /
                    100
                  ).toFixed(2)}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Based on {formData.businessUsePct}% business use
                </p>
              </div>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <span className="text-gray-600 block mb-1">Deductible Amount:</span>
                <span className="text-xl font-bold text-blue-700">
                  $
                  {(
                    (safeParseFloat(formData.amount) * safeParseFloat(formData.businessUsePct)) /
                    100
                  ).toFixed(2)}
                </span>
                <p className="text-xs text-gray-500 mt-1">For income tax deduction</p>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-700 bg-white bg-opacity-60 rounded-md p-2">
              <strong>Note:</strong> Your business use % directly impacts your claimable ITC. Higher
              percentages yield more ITC but increase audit scrutiny.
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={
              isSubmitting ||
              uploadState.status === 'uploading' ||
              uploadState.status === 'compressing'
            }
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={
              isSubmitting ||
              isLoading ||
              !canWrite ||
              uploadState.status === 'uploading' ||
              uploadState.status === 'compressing'
            }
          >
            {isSubmitting ||
            uploadState.status === 'uploading' ||
            uploadState.status === 'compressing' ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                {uploadState.status === 'compressing' && 'Compressing...'}
                {uploadState.status === 'uploading' && 'Uploading...'}
                {isSubmitting &&
                  uploadState.status !== 'compressing' &&
                  uploadState.status !== 'uploading' &&
                  (isEditing ? 'Updating...' : 'Saving...')}
              </>
            ) : !canWrite ? (
              'Read-Only Mode'
            ) : isEditing ? (
              'Update Expense'
            ) : (
              'Add Expense'
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default ExpenseForm;
